<template>
  <section v-if="!isOpened" class="button-container">
    <ButtonView
      v-if="config.showOpenButton"
      class="button is-link is-light has-border-primary has-background-white open-button"
      icon-pack="cls"
      icon-left="cls-checklist"
      @click="setPanelState(true)"
      :data-e2e="SidePanelCypressAttribute.buttons.OPEN_PANEL_BUTTON"
    >
      <p class="open-button-text">{{ config.openLabel }}</p>
    </ButtonView>
  </section>
  <section
    v-else
    class="is-absolute panel-content xy-t-0 p-4 has-background-white"
    :class="{
      'is-left': config.isLeft,
      'is-right': config.isRight,
      'has-overlay': config.hasFullPage,
      'has-full-page': config.hasFullPage
    }"
    :data-e2e="SidePanelCypressAttribute.views.PANEL_CONTENT"
    @wheel="handleOverlayScroll"
  >
    <section
      class="panel-body"
      :class="{
        'has-full-page': config.hasFullPage,
        'is-left': config.isLeft,
        'is-right': config.isRight,
        'panel-content': config.hasFullPage
      }"
    >
      <transition name="show">
        <section class="is-flex is-flex-direction-column is-full-height">
          <div class="is-flex is-align-items-center">
            <slot name="header" />
            <ButtonView
              v-if="config.showCloseButton"
              class="button is-link is-light has-background-white is-borderless close-button"
              icon-pack="cls"
              icon-left="cls-arrow-back"
              @click="onLeave"
              :data-e2e="SidePanelCypressAttribute.buttons.CLOSE_MODAL_BUTTON"
            >
              {{ config.closeLabel }}
            </ButtonView>
            <ButtonView
              class="button is-link is-light has-background-white is-borderless close-button ml-auto m-r-10 m-t-10"
              icon-pack="cls"
              icon-left="cls-remove"
              @click="setPanelState(false)"
              :data-e2e="SidePanelCypressAttribute.buttons.CLOSE_PANEL_BUTTON"
            ></ButtonView>
          </div>
          <section
            ref="scrollableContent"
            class="no-scrollbar is-overflow-y-auto is-flex-grow-1 scrollable-content"
            :style="scrollableContentStyle"
          >
            <slot name="content" />
          </section>
          <slot name="footer" />
        </section>
      </transition>
    </section>
  </section>
</template>

<script>
import { SidePanelConfig } from '@/modules/core/SidePanel/configs/SidePanelConfig';
import { SidePanelCypressAttribute } from '@/modules/core/SidePanel/constants/SidePanelCypressAttribute';
import { SidePanelEvent } from '@/modules/core/SidePanel/constants/SidePanelEvent';
import ButtonView from '@/modules/core/ui/components/wrappers/ButtonView.vue';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'SidePanel',
  emits: ['on-toggle-side-panel', 'on-exit'],
  slotsDefinition: {
    header: {},
    content: {},
    footer: {}
  },
  components: { ButtonView },
  props: {
    config: {
      type: SidePanelConfig,
      default: () => new SidePanelConfig()
    }
  },
  static: {
    SidePanelCypressAttribute
  },
  events() {
    return {
      [SidePanelEvent.OPEN]: () => this.setPanelState(true),
      [SidePanelEvent.CLOSE]: () => this.setPanelState(false)
    };
  },
  data() {
    return {
      isOpened: this.config.open,
      panelWidth: this.config.width
    };
  },
  computed: {
    isLeft() {
      return this.config.position === 'left';
    },
    isRight() {
      return this.config.position === 'right';
    },
    scrollableContentStyle() {
      return this.config.hasFullPage ? {} : { maxHeight: 'var(--ce-fit-height-50px)' };
    }
  },
  methods: {
    onLeave() {
      this.$emit('on-exit');
    },
    setPanelState(open) {
      if (this.config.hasFullPage) {
        document.body.style.overflow = open ? 'hidden' : '';

        if (open) {
          this.addGlobalScrollListener();
        } else {
          this.removeGlobalScrollListener();
        }
      }

      this.isOpened = open;
      this.$emit('on-toggle-side-panel', this.isOpened);
    },
    close() {
      this.setPanelState(false);
    },
    handleOverlayScroll(event) {
      if (!this.config.hasFullPage || !this.isOpened) {
        return;
      }

      const panelBody = event.target.closest('.panel-body');
      if (!panelBody && this.$refs.scrollableContent) {
        event.preventDefault();

        const scrollableContent = this.$refs.scrollableContent;
        const scrollDelta = event.deltaY;

        scrollableContent.scrollTop += scrollDelta;
      }
    },
    addGlobalScrollListener() {
      document.addEventListener('wheel', this.handleGlobalScroll, { passive: false });
    },
    removeGlobalScrollListener() {
      document.removeEventListener('wheel', this.handleGlobalScroll);
    },
    handleGlobalScroll(event) {
      if (!this.config.hasFullPage || !this.isOpened) {
        return;
      }

      const panelContent = event.target.closest('.panel-content');
      if (!panelContent && this.$refs.scrollableContent) {
        event.preventDefault();

        const scrollableContent = this.$refs.scrollableContent;
        const scrollDelta = event.deltaY;

        scrollableContent.scrollTop += scrollDelta;
      }
    }
  },
  mounted() {
    if (this.config.hasFullPage && this.isOpened) {
      document.body.style.overflow = 'hidden';
      this.addGlobalScrollListener();
    }
  },
  beforeUnmount() {
    if (this.config.hasFullPage) {
      document.body.style.overflow = '';
      this.removeGlobalScrollListener();
    }
  }
};
</script>

<style scoped>
.close-button {
  justify-content: center !important;

  :deep(.icon),
  :deep(.icon i) {
    color: inherit !important;
    transition: none !important;
  }

  &:not(.ml-auto) :deep(.icon:first-child:not(:last-child)) {
    margin-right: var(--ce-5px) !important;
  }

  &:hover {
    background-color: var(--ce-primary) !important;
    color: var(--ce-white) !important;
  }
}

.close-button.ml-auto {
  width: var(--ce-32px);
  height: var(--ce-32px);
  padding: 0 !important;

  :deep(.icon:first-child:not(:last-child)) {
    margin-right: 0 !important;
  }
}

.open-button {
  z-index: var(--ce-z-index-sticky-header);

  &:hover {
    background-color: var(--ce-primary-light) !important;
    border: 1px solid var(--ce-primary) !important;
  }
}

.panel-content {
  width: v-bind(panelWidth);
  min-width: var(--ce-325px);
  box-shadow: 0 var(--ce-4px) var(--ce-12px) rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;
  z-index: var(--ce-z-index-sticky-header);
}

.show-enter,
.show-leave-to {
  width: 0;
  padding: 0;
}

.show-enter-active,
.show-leave-active {
  opacity: 0;
}

.has-full-page {
  position: fixed !important;
  top: 0;
  bottom: 0;
  overflow: auto;
}
.has-overlay {
  width: 100% !important;
  height: 100% !important;
  background-color: rgba(0, 0, 0, 0.2) !important;
}
.is-left {
  left: 0 !important;
}
.is-right {
  right: 0 !important;
}
.panel-body {
  top: 0 !important;
  background-color: var(--ce-white);
}

@media (max-width: CE_TABLET) {
  .panel-content {
    width: 100% !important;
    min-width: 0 !important;
  }
  .open-button {
    width: var(--ce-40px);
    overflow-x: hidden;
    height: var(--ce-40px);
  }
  .open-button-text {
    display: none;
  }
  .panel-content {
    position: fixed !important;
    top: 0;
    bottom: 0;
    overflow: auto;
  }
}
</style>
